'use client';

import { useEffect, useRef } from 'react';
import type { PlatformEventEnvelope, PlatformEventType } from '@hai/shared';

const gatewayApi =
  (process.env.NEXT_PUBLIC_GATEWAY_URL || 'http://localhost:4100/api').replace(
    /\/$/,
    '',
  );

type EventHandler = (event: PlatformEventEnvelope) => void;

export function useGatewayEvents(
  eventTypes: PlatformEventType[],
  handler: EventHandler,
) {
  const handlerRef = useRef(handler);
  const eventKey = eventTypes.join('|');

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const source = new EventSource(`${gatewayApi}/events/stream`);
    const listeners = eventTypes.map((eventType) => {
      const listener = (message: MessageEvent<string>) => {
        try {
          const data = JSON.parse(message.data) as PlatformEventEnvelope;
          handlerRef.current(data);
        } catch (error) {
          console.warn('Failed to parse gateway event', error);
        }
      };

      source.addEventListener(eventType, listener);
      return { eventType, listener };
    });

    source.onerror = () => {
      // Let the browser handle reconnects; we only surface debug noise in dev tools.
    };

    return () => {
      for (const { eventType, listener } of listeners) {
        source.removeEventListener(eventType, listener);
      }
      source.close();
    };
  }, [eventKey]);
}
